import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Bell, Globe, ShieldCheck, Star, HelpCircle, Lock, FileText, ChevronRight, Sparkles, Play, Loader2, Video, Share2 } from "lucide-react";
import { toast } from "sonner";
import BedtimeSetting from "@/components/BedtimeSetting";
import AdelaidaWelcome from "@/components/AdelaidaWelcome";
import { AnimatePresence } from "framer-motion";

const pref = (k, d) => { try { const v = localStorage.getItem(k); return v === null ? d : v === "true"; } catch { return d; } };

const VOICES = [
  { id: "sage", label: "Adelaida", desc: "Warm & calm · her face" },
  { id: "onyx", label: "Richard", desc: "Deep & grounded · his face" },
  { id: "shimmer", label: "Shimmer", desc: "Soft & gentle" },
  { id: "nova", label: "Nova", desc: "Bright & warm" },
  { id: "coral", label: "Coral", desc: "Friendly & expressive" },
];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [notifs, setNotifs] = useState(() => pref("mirro_notifs", true));
  const [dataShare, setDataShare] = useState(() => pref("mirro_datashare", true));
  const [voice, setVoice] = useState(user?.voice || "sage");
  const [welcome, setWelcome] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const isPremium = user?.plan === "premium" || user?.plan === "pro";
  const [liveFace, setLiveFace] = useState(user?.voice_visual === "avatar");

  useEffect(() => { api.get("/usage").then((r) => setUsage(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user?.voice) setVoice(user.voice); }, [user?.voice]);
  useEffect(() => { setLiveFace(user?.voice_visual === "avatar"); }, [user?.voice_visual]);

  const toggleLiveFace = async (on) => {
    setLiveFace(on);
    try { await api.post("/settings/voice-visual", { mode: on ? "avatar" : "orb" }); await refreshUser(); toast.success(on ? "Live face on by default for voice calls." : "Voice calls will use the glowing orb."); }
    catch { setLiveFace(!on); toast.error("Could not save that."); }
  };

  const chooseVoice = async (id) => {
    setVoice(id);
    try { await api.post("/settings/voice", { voice: id }); await refreshUser(); toast.success(`Your coach is now ${["onyx", "echo", "ash", "fable"].includes(id) ? "Richard" : "Adelaida"}.`); }
    catch { toast.error("Could not save voice."); }
  };
  const preview = async (id) => {
    setPreviewing(id);
    try {
      const { data } = await api.post("/tts", { text: "Hey, I'm really glad you showed up today. Let's take a breath and begin.", voice: id });
      if (data.url) { const a = new Audio(`${BACKEND}${data.url}`); a.onended = () => setPreviewing(null); await a.play(); }
      else { setPreviewing(null); toast.error("You're out of talk time this week."); }
    } catch { setPreviewing(null); toast.error("Could not play preview."); }
  };
  const setP = (k, v, fn) => { try { localStorage.setItem(k, String(v)); } catch {} fn(v); };

  const limitMin = usage?.talk_minutes_limit ?? 0;
  const usedSec = usage?.talk_seconds_used ?? 0;
  const bonusSec = usage?.talk_bonus_seconds ?? 0;
  const bonusMin = Math.round(bonusSec / 60);
  const unlimitedTalk = (usage?.talk_remaining_seconds ?? 0) >= 100000000;
  const weeklyLeftMin = Math.max(0, Math.round(((usage?.talk_remaining_seconds ?? 0) - bonusSec) / 60));
  const usedPct = limitMin > 0 ? Math.min(100, (usedSec / (limitMin * 60)) * 100) : 0;

  const supportItems = [
    { icon: Star, label: "Leave a Review", onClick: () => { window.location.href = "mailto:bookings@michaelmuller.co.uk?subject=My%20MiRRo%20review"; } },
    { icon: HelpCircle, label: "Get Help", onClick: () => { window.location.href = "mailto:bookings@michaelmuller.co.uk?subject=Xcelio%20help"; } },
    { icon: Lock, label: "Privacy Policy", onClick: () => navigate("/legal#privacy") },
    { icon: FileText, label: "Terms & Conditions", onClick: () => navigate("/legal#terms") },
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[560px] h-[560px] rounded-full opacity-25"
        style={{ background: "radial-gradient(circle, hsl(195 100% 55% / 0.4) 0%, transparent 62%)", filter: "blur(70px)" }} />
      <Navbar />
      <main className="relative max-w-lg mx-auto px-5 sm:px-8 pt-8 pb-24">
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-8 text-center">Settings</h1>

        {/* Talk time */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="glass rounded-3xl p-6 mb-8 border border-white/10" data-testid="settings-talk-time">
          <div className="flex items-start justify-between mb-1">
            <span className="text-xs uppercase tracking-[0.2em] text-primary font-medium">Talk time</span>
            <span className="text-xs text-muted-foreground" data-testid="talk-refill-label">{!usage ? null : usage.talk_period === "month" ? `refills ${usage?.talk_refills_at ? new Date(usage.talk_refills_at).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "monthly"}` : "refills weekly"}</span>
          </div>
          <div className="flex items-baseline gap-2 mb-4">
            {unlimitedTalk ? (
              <span className="font-serif text-5xl tracking-tight" data-testid="talk-left">Unlimited</span>
            ) : (
              <>
                <span className="font-serif text-5xl tracking-tight" data-testid="talk-left">{weeklyLeftMin}</span>
                <span className="text-muted-foreground">of {limitMin} min left</span>
              </>
            )}
          </div>
          {!unlimitedTalk && bonusMin > 0 && (
            <p className="text-sm text-primary mb-3 -mt-2" data-testid="talk-bonus">+ {bonusMin} top-up min · never expire</p>
          )}
          {!unlimitedTalk && (
            <div className="h-2.5 rounded-full bg-muted overflow-hidden mb-4">
              <motion.div initial={{ width: 0 }} animate={{ width: `${usedPct}%` }} transition={{ duration: 1 }}
                className="h-full rounded-full bg-gradient-to-r from-coral-400 to-secondary" />
            </div>
          )}
          <div className="flex justify-end gap-2">
            {user?.plan === "premium" && (
              <Button size="sm" variant="ghost" className="rounded-full" data-testid="talk-manage" onClick={() => navigate("/account")}>Manage plan</Button>
            )}
            <Button size="sm" className="rounded-full bg-foreground text-background hover:opacity-90" data-testid="talk-get-more" onClick={() => navigate("/topup")}>
              Get more
            </Button>
          </div>
        </motion.div>

        {/* Coach voice picker */}
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70 mb-3 px-1">Coach voice</p>
        <div className="rounded-3xl border border-border bg-card/50 p-4 mb-8" data-testid="settings-voice">
          <div className="grid grid-cols-2 gap-2.5">
            {VOICES.map((v) => {
              const active = voice === v.id;
              return (
                <div key={v.id} className={`flex items-center gap-2 rounded-2xl border px-3 py-2.5 transition-colors ${active ? "border-primary bg-primary/10" : "border-border"}`}>
                  <button onClick={() => chooseVoice(v.id)} data-testid={`voice-${v.id}`} className="flex-1 text-left">
                    <span className={`block text-sm font-medium ${active ? "text-primary" : ""}`}>{v.label}</span>
                    <span className="block text-xs text-muted-foreground">{v.desc}</span>
                  </button>
                  <button onClick={() => preview(v.id)} data-testid={`voice-preview-${v.id}`} aria-label={`Preview ${v.label}`}
                    className="w-8 h-8 rounded-full flex items-center justify-center bg-muted hover:bg-primary/15 text-foreground shrink-0">
                    {previewing === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Preferences */}
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70 mb-3 px-1">Preferences</p>
        <div className="rounded-3xl border border-border bg-card/50 divide-y divide-border mb-8" data-testid="settings-preferences">
          <Row icon={Bell} label="Notifications">
            <Switch checked={notifs} onCheckedChange={(v) => setP("mirro_notifs", v, setNotifs)} data-testid="pref-notifs" />
          </Row>
          <BedtimeSetting />
          <button onClick={() => navigate("/memory")} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/5 transition-colors" data-testid="settings-memory">
            <ShieldCheck className="w-5 h-5 text-muted-foreground shrink-0" strokeWidth={1.5} />
            <span className="flex-1 min-w-0"><span className="block text-sm">What Xcelio remembers</span><span className="block text-xs text-muted-foreground mt-0.5">See, edit or delete her memory of you</span></span>
            <ChevronRight className="w-4 h-4 text-muted-foreground/60" />
          </button>
          <Row icon={Globe} label="Language">
            <span className="text-sm text-muted-foreground flex items-center gap-1">English <ChevronRight className="w-4 h-4" /></span>
          </Row>
          <Row icon={ShieldCheck} label="AI Data Sharing">
            <Switch checked={dataShare} onCheckedChange={(v) => setP("mirro_datashare", v, setDataShare)} data-testid="pref-datashare" />
          </Row>
          {isPremium && (
            <Row icon={Video} label="Live face on voice calls" sub="Xcelio appears as Adelaida's face by default — free, no extra minutes">
              <Switch checked={liveFace} onCheckedChange={toggleLiveFace} data-testid="pref-live-face" />
            </Row>
          )}
        </div>

        <button onClick={() => setWelcome(true)} className="w-full flex items-center gap-3 px-5 py-4 mb-8 rounded-3xl border border-border bg-card/50 text-left hover:bg-white/5 transition-colors" data-testid="settings-hear-welcome">
          <Play className="w-5 h-5 text-primary shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="block text-sm">Hear {["onyx", "echo", "ash", "fable"].includes(voice) ? "Richard" : "Adelaida"}'s welcome</span>
            <span className="block text-xs text-muted-foreground mt-0.5">Why Xcelio is different, and what to expect</span>
          </span>
          <ChevronRight className="w-4 h-4 text-muted-foreground/60" />
        </button>
        <AnimatePresence>{welcome && <AdelaidaWelcome key="aw" onClose={() => setWelcome(false)} />}</AnimatePresence>

        {/* Share / promo */}
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70 mb-3 px-1">Share</p>
        <button onClick={() => navigate("/partner")} className="w-full flex items-center gap-3 px-5 py-4 mb-3 rounded-3xl border border-primary/30 bg-primary/[0.05] text-left hover:bg-white/5 transition-colors" data-testid="settings-partner">
          <Star className="w-5 h-5 text-primary shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="block text-sm">Share + Partner</span>
            <span className="block text-xs text-muted-foreground mt-0.5">Your invite link — earn 25% of their membership for their first 12 months on anyone who joins</span>
          </span>
          <ChevronRight className="w-4 h-4 text-muted-foreground/60" />
        </button>
        <button onClick={() => navigate("/promo")} className="w-full flex items-center gap-3 px-5 py-4 mb-8 rounded-3xl border border-border bg-card/50 text-left hover:bg-white/5 transition-colors" data-testid="settings-promo">
          <Share2 className="w-5 h-5 text-muted-foreground shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="block text-sm">Meet your coach</span>
            <span className="block text-xs text-muted-foreground mt-0.5">Grab a ready-to-post graphic with Adelaida's face for Instagram / TikTok</span>
          </span>
          <ChevronRight className="w-4 h-4 text-muted-foreground/60" />
        </button>

        {/* Support */}
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70 mb-3 px-1">Support</p>
        <div className="rounded-3xl border border-border bg-card/50 divide-y divide-border mb-8">
          {supportItems.map((it) => (
            <button key={it.label} onClick={it.onClick} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/5 transition-colors" data-testid={`support-${it.label.split(" ")[0].toLowerCase()}`}>
              <it.icon className="w-5 h-5 text-muted-foreground shrink-0" />
              <span className="flex-1 text-sm">{it.label}</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground/60" />
            </button>
          ))}
        </div>

        {user?.is_admin && (
          <button onClick={() => navigate("/admin")} className="w-full flex items-center gap-3 px-5 py-4 rounded-3xl border border-primary/20 bg-primary/5 text-primary text-sm" data-testid="settings-admin">
            <Sparkles className="w-5 h-5" /> Admin & cost meter <ChevronRight className="w-4 h-4 ml-auto" />
          </button>
        )}
      </main>
    </div>
  );
}

function Row({ icon: Icon, label, sub, children }) {
  return (
    <div className="flex items-center gap-3 px-5 py-4">
      <Icon className="w-5 h-5 text-muted-foreground shrink-0" />
      <span className="flex-1 min-w-0">
        <span className="block text-sm">{label}</span>
        {sub && <span className="block text-xs text-muted-foreground mt-0.5">{sub}</span>}
      </span>
      {children}
    </div>
  );
}
